import { defineStore, storeToRefs } from 'pinia'
import { ref, watch } from 'vue'
import { useSuiteStore } from '@/stores/useSuiteStore'
import { useUserStore } from '@/stores/useUserStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'

export interface Variable {
  [key: string]: any
}

export const useVariableStore = defineStore('variable', () => {
  const variables = ref<Variable[]>([])
  const currentVariable = ref<Variable | null>(null)
  const dateSelected = ref<Date>(new Date())
  const viewYearly = ref<boolean>(false)
  const loading = ref(false)
  const loadingUpdate = ref(false)
  const selectedUserUuid = ref<string | null>(null)

  const suiteStore = useSuiteStore()
  const userStore = useUserStore()
  const boostCoreStore = useBoostCorePermissionStore()
  const { userConnected } = storeToRefs(userStore)
  const isManager = storeToRefs(boostCoreStore).isManager

  watch(dateSelected, async () => {
    await fetchMyVariables()
  })

  watch(selectedUserUuid, async () => {
    await fetchMyVariables()
  })

  async function fetchMyVariables() {
    loading.value = true
    try {
      const body: Record<string, unknown> = {
        period: dateSelected.value.toISOString().slice(0, 10),
        yearly: viewYearly.value,
      }
      if (selectedUserUuid.value) body.userUuid = selectedUserUuid.value
      const viewAsUuid = selectedUserUuid.value

      let variableData: any[] = []
      const shouldFetchBoth = (isManager.value || boostCoreStore.include('SUPER_ADMIN')) && viewAsUuid
      if (shouldFetchBoth) {
        const [meRes, managerRes] = await Promise.all([
          suiteStore.suiteFetch('variables/me', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(body),
          }),
          suiteStore.suiteFetch('variables/manager/me', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(body),
          }),
        ])
        const meData = meRes && typeof meRes !== 'boolean' && meRes.ok ? await meRes.json() : []
        const managerData = managerRes && typeof managerRes !== 'boolean' && managerRes.ok ? await managerRes.json() : []
        const meArr = Array.isArray(meData) ? meData : [meData]
        variableData = [...meArr, ...(Array.isArray(managerData) ? managerData : [])]
      } else {
        const url = isManager.value ? 'variables/manager/me' : 'variables/me'
        const variableResponse = await suiteStore.suiteFetch(url, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(body),
        })
        if (!variableResponse || typeof variableResponse === 'boolean' || !variableResponse.ok) throw new Error(`Echec de la recuperation variables`)
        variableData = await variableResponse.json()
        variableData = Array.isArray(variableData) ? variableData : [variableData]
      }

      const isCurrentUser = (v: any) => {
        if (viewAsUuid) return v.salesEngineer?.uuid === viewAsUuid
        return v.salesEngineer?.email === userConnected.value?.email || v.salesEngineer?.uuid === boostCoreStore.user?.uuid
      }

      variables.value = variableData.filter((v: any) => !isCurrentUser(v))

      currentVariable.value = variableData.find((v: any) => {
        const period = new Date(v.period)
        const inPeriod = period.getFullYear() === dateSelected.value.getFullYear() && period.getMonth() === dateSelected.value.getMonth()
        return inPeriod && isCurrentUser(v)
      }) ?? null
      loading.value = false
    } catch (error) {
      loading.value = false
      console.error('Erreur lors de la récupération des variables:', error)
    }
  }

  async function reloadVariable(variableUuid: Number) {
    loadingUpdate.value = true
    const body = JSON.stringify({
      uuid: variableUuid,
    })
    const variableReloadResponse = await suiteStore.suiteFetch('/variables/reload', {
      method: 'PUT',
      headers: {
        'content-type': 'application/json',
      },
      credentials: 'include',
      body: body,
    })
    if (!variableReloadResponse || !variableReloadResponse.ok) throw new Error(`Echec de la recuperation variables`)

    currentVariable.value = await variableReloadResponse.json()
    loadingUpdate.value = false
  }

  async function validateVariable(variableUuid: Number, validationManager = false) {
    loadingUpdate.value = true
    const body = JSON.stringify({
      uuid: variableUuid,
      validationManager,
    })
    const variableValidatedResponse = await suiteStore.suiteFetch('/variables/validated', {
      method: 'PUT',
      headers: {
        'content-type': 'application/json',
      },
      credentials: 'include',
      body: body,
    })
    if (!variableValidatedResponse || !variableValidatedResponse.ok) throw new Error(`Echec de la recuperation variables`)
    await fetchMyVariables()
    loadingUpdate.value = false
  }

  async function fetchVariablesForYear(year: number): Promise<Variable[]> {
    try {
      const viewAsUuid = selectedUserUuid.value

      let variableData: any[] = []
      if (viewAsUuid) {
        // Manager/admin consulte un commercial : utiliser by-period pour récupérer toutes ses variables
        const res = await suiteStore.suiteFetch('variables/by-period', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ usersUuid: [viewAsUuid] }),
        })
        if (!res || typeof res === 'boolean' || !res.ok) return []
        variableData = await res.json()
        variableData = Array.isArray(variableData) ? variableData : [variableData]
      } else {
        // Commercial consulte ses propres variables
        const period = new Date(year, 0, 1)
        const res = await suiteStore.suiteFetch('variables/me', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ period: period.toISOString().slice(0, 10), yearly: true }),
        })
        if (!res || typeof res === 'boolean' || !res.ok) return []
        variableData = await res.json()
        variableData = Array.isArray(variableData) ? variableData : [variableData]
      }

      return variableData.filter((v: any) => new Date(v.period).getFullYear() === year)
    } catch (error) {
      console.error('Erreur fetchVariablesForYear:', error)
      return []
    }
  }

  async function fetchPendingValidation(): Promise<Variable[]> {
    if (!isManager.value) return []
    const res = await suiteStore.suiteFetch('variables/manager/pending-validation', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({}),
    })
    if (!res || typeof res === 'boolean' || !res.ok) return []
    const data = await res.json()
    return Array.isArray(data) ? data : []
  }

  async function fetchLuccaAdmin(filters: { statuses?: string[]; userUuids?: string[]; bus?: string[] } = {}): Promise<Variable[]> {
    const res = await suiteStore.suiteFetch('/variables/admin/by-status', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(filters),
    })
    if (!res || typeof res === 'boolean' || !res.ok) return []
    const data = await res.json()
    return Array.isArray(data) ? data : []
  }

  async function sendBatchToLucca(uuids: string[]): Promise<{
    sent: { uuid: string; luccaVariableId: number | null }[]
    failed: { uuid: string; error: string }[]
  }> {
    const res = await suiteStore.suiteFetch('/variables/admin/send-to-lucca', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ uuids }),
    })
    if (!res || typeof res === 'boolean' || !res.ok) {
      return { sent: [], failed: uuids.map((uuid) => ({ uuid, error: 'Requête en échec' })) }
    }
    return res.json()
  }

  async function deleteFromLuccaById(luccaVariableId: number): Promise<{
    reset: string[]
    failed: { luccaVariableId: number; variableUuid?: string; error: string }[]
    notFound: number[]
  }> {
    const res = await suiteStore.suiteFetch('/variables/lucca/delete-by-lucca-ids', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ luccaVariableIds: [luccaVariableId] }),
    })
    if (!res || typeof res === 'boolean' || !res.ok) {
      return { reset: [], failed: [{ luccaVariableId, error: 'Requête en échec' }], notFound: [] }
    }
    return res.json()
  }

  async function updateVariable(data: { uuid: string; recovery?: number | undefined; complement?: number | undefined }) {
    loadingUpdate.value = true
    const body = JSON.stringify({
      ...data,
    })
    const variableUpdatedResponse = await suiteStore.suiteFetch('/variables', {
      method: 'PUT',
      headers: {
        'content-type': 'application/json',
      },
      credentials: 'include',
      body: body,
    })

    if (!variableUpdatedResponse || !variableUpdatedResponse.ok) throw new Error(`Echec de la recuperation variables`)

    loadingUpdate.value = false
  }

  return {
    dateSelected,
    variables,
    currentVariable,
    loading,
    loadingUpdate,
    selectedUserUuid,
    fetchMyVariables,
    fetchVariablesForYear,
    fetchPendingValidation,
    reloadVariable,
    validateVariable,
    updateVariable,
    fetchLuccaAdmin,
    sendBatchToLucca,
    deleteFromLuccaById,
  }
})
